/**
 * Error Reporter — Admin Panel
 *
 * Fire-and-forget error reporting to the backend error alerting system.
 * Sends error details to POST /v1/errors/report which then alerts to ClickUp.
 *
 * IMPORTANT: This must never block user operations or throw errors.
 */

import axios from 'axios'
import { deriveFeatureFromPath } from 'services/errorTracking'

interface ReportErrorParams {
    code: string
    errorMessage: string
    severity?: 'critical' | 'warning'
    stackTrace?: string
    feature?: string
    metadata?: Record<string, unknown>
    requestContext?: {
        method?: string
        url?: string
        statusCode?: number
        responseBody?: string
    }
}

/**
 * Report an error to the backend alerting system.
 * Non-blocking: fires and forgets, never throws.
 */
export function reportError(params: ReportErrorParams): void {
    axios
        .post('/errors/report', {
            code: params.code,
            errorMessage: params.errorMessage,
            severity: params.severity || 'critical',
            stackTrace: params.stackTrace?.slice(0, 500),
            feature: params.feature || deriveFeatureFromPath(window.location.pathname),
            browserInfo: navigator.userAgent,
            metadata: params.metadata,
            requestContext: params.requestContext,
            source: 'admin',
            timestamp: new Date().toISOString(),
        })
        .catch(() => {
            // Silent fail — never block user operations
        })
}

/** Map URL patterns to error codes for automatic 5xx reporting */
export function mapUrlToErrorCode(url: string): string {
    if (url.includes('/email/') || url.includes('/ext/loads-with-email')) return 'ERR_EMAIL_01'
    if (url.includes('/factoring/') || url.includes('/rts/') || url.includes('/triumph/') || url.includes('/wex/')) return 'ERR_FACT_01'
    if (url.includes('/auth/refresh-token')) return 'ERR_AUTH_04'
    if (url.includes('/auth/')) return 'ERR_AUTH_03'
    if (url.includes('/agentic-hub/') || url.includes('/updater/')) return 'ERR_AIHUB_01'
    if (url.includes('/load/') || url.includes('/loads/')) return 'ERR_LOAD_01'
    return 'ERR_GENERIC_01'
}
